import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Shield, Eye, EyeOff, Save, CheckCircle, Clock, AlertCircle, XCircle, WifiOff, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'connected', label: 'Connected', color: 'bg-green-900/30 text-green-400' },
  { value: 'pending', label: 'Pending', color: 'bg-amber-50 text-amber-400' },
  { value: 'expired', label: 'Expired', color: 'bg-red-900/30 text-red-400' },
  { value: 'error', label: 'Error', color: 'bg-red-900/30 text-red-400' },
  { value: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-600' },
];

const PLATFORM_COLORS = {
  facebook: '#1877f2', instagram: '#e4405f', linkedin: '#0a66c2', tiktok: '#000000',
  gbp: '#4285f4', youtube: '#ff0000', rumble: '#85c742', truth_social: '#5448ee',
};

const PLATFORM_EXTRA_FIELDS = {
  facebook: [{ key: 'page_id', label: 'Page ID' }, { key: 'page_name', label: 'Page Name' }],
  instagram: [{ key: 'ig_business_id', label: 'IG Business ID' }, { key: 'ig_username', label: 'IG Username' }],
  linkedin: [{ key: 'company_page_id', label: 'Company Page ID' }, { key: 'company_name', label: 'Company Name' }],
  tiktok: [{ key: 'username', label: 'Username' }],
  gbp: [{ key: 'location_id', label: 'Location ID' }, { key: 'account_id', label: 'Account ID' }],
  youtube: [{ key: 'channel_id', label: 'Channel ID' }, { key: 'channel_name', label: 'Channel Name' }],
  rumble: [{ key: 'channel_url', label: 'Channel URL' }],
  truth_social: [{ key: 'handle', label: 'Handle' }],
};

export default function AdminCredentialVault({ slug, clientName }) {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showPw, setShowPw] = useState({});
  const [edits, setEdits] = useState({});

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  });

  const fetchCreds = () => {
    fetch(`${API}/api/admin/dashboard-clients/${slug}/credentials`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => {
        setCredentials(d.credentials || []);
        const e = {};
        (d.credentials || []).forEach(c => {
          e[c.platform] = { ...c };
        });
        setEdits(e);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCreds(); }, [slug]);

  const updateField = (platform, field, value) => {
    setEdits(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
  };

  const updateExtra = (platform, key, value) => {
    setEdits(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        extra_fields: { ...(prev[platform]?.extra_fields || {}), [key]: value },
      },
    }));
  };

  const savePlatform = async (platform) => {
    setSaving(platform);
    try {
      const data = edits[platform] || {};
      await fetch(`${API}/api/admin/dashboard-clients/${slug}/credentials/${platform}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(data),
      });
      fetchCreds();
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="text-gray-600 animate-pulse py-6 text-center text-sm">Loading credentials...</div>;

  return (
    <div className="space-y-3" data-testid="admin-credential-vault">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="h-4 w-4 text-blue-400" />
        <h3 className="text-sm font-semibold text-gray-900">Credential Vault — {clientName}</h3>
        <Badge className="bg-blue-900/20 text-blue-400 text-[10px]">AES-256 Encrypted</Badge>
      </div>

      {credentials.map(cred => {
        const platform = cred.platform;
        const edit = edits[platform] || {};
        const color = PLATFORM_COLORS[platform] || '#6b7280';
        const extras = PLATFORM_EXTRA_FIELDS[platform] || [];
        const statusCfg = STATUS_OPTIONS.find(s => s.value === (edit.oauth_status || 'not_configured'));

        return (
          <Card key={platform} className="bg-[#0d1321] border-gray-800" data-testid={`cred-${platform}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                  <span className="text-xs font-bold" style={{ color }}>{cred.platform.charAt(0).toUpperCase()}</span>
                </div>
                <span className="text-sm font-medium text-gray-900 capitalize">{platform.replace('_', ' ')}</span>
                <Badge className={`text-[10px] ml-auto ${statusCfg?.color || 'bg-gray-100 text-gray-600'}`}>
                  {statusCfg?.label || 'Unknown'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] text-gray-500">Login Email</Label>
                  <Input value={edit.email || ''} onChange={e => updateField(platform, 'email', e.target.value)}
                    className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" placeholder="email@example.com" />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Password</Label>
                  <div className="relative">
                    <Input value={edit.password || ''} onChange={e => updateField(platform, 'password', e.target.value)}
                      type={showPw[platform] ? 'text' : 'password'}
                      className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5 pr-8" placeholder="••••••••" />
                    <button onClick={() => setShowPw(p => ({ ...p, [platform]: !p[platform] }))}
                      className="absolute right-2 top-1.5 text-gray-500 hover:text-gray-600">
                      {showPw[platform] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">OAuth Status</Label>
                  <select value={edit.oauth_status || 'not_configured'} onChange={e => updateField(platform, 'oauth_status', e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 text-xs rounded-md px-2 h-8 mt-0.5">
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                {extras.map(ex => (
                  <div key={ex.key}>
                    <Label className="text-[10px] text-gray-500">{ex.label}</Label>
                    <Input value={edit.extra_fields?.[ex.key] || ''} onChange={e => updateExtra(platform, ex.key, e.target.value)}
                      className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" />
                  </div>
                ))}
                <div className="md:col-span-2 lg:col-span-3">
                  <Label className="text-[10px] text-gray-500">Notes</Label>
                  <Input value={edit.notes || ''} onChange={e => updateField(platform, 'notes', e.target.value)}
                    className="bg-gray-50 border-gray-200 text-gray-900 text-xs h-8 mt-0.5" placeholder="Internal notes..." />
                </div>
              </div>

              <div className="flex justify-end mt-3">
                <Button size="sm" onClick={() => savePlatform(platform)} disabled={saving === platform}
                  className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-3" data-testid={`save-cred-${platform}`}>
                  {saving === platform ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                  {saving === platform ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
